import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Inbox, MessageSquare, RefreshCw, Loader2, Mail, Phone, Calendar } from "lucide-react";
import { toast } from "sonner";
import { adminApi, clearAdminToken, getAdminToken } from "../lib/api";
import Logo from "../components/Logo";
import { useSeo } from "../lib/seo";

const STATUS_OPTIONS = ["new", "in-progress", "closed"];

export default function AdminDashboard() {
  useSeo({ title: "Enquiries · Valora Admin", description: "Admin dashboard" });
  const navigate = useNavigate();
  const [tab, setTab] = useState("enquiries");
  const [enquiries, setEnquiries] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!getAdminToken()) { navigate("/admin/login"); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      const [e, c] = await Promise.all([
        adminApi.get("/admin/enquiries"),
        adminApi.get("/admin/contacts"),
      ]);
      setEnquiries(e.data);
      setContacts(c.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        clearAdminToken();
        navigate("/admin/login");
      } else {
        toast.error("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  };

  const logout = () => { clearAdminToken(); navigate("/admin/login"); };

  const updateStatus = async (id, status) => {
    try {
      await adminApi.patch(`/admin/enquiries/${id}/status?status=${status}`);
      setEnquiries((list) => list.map((e) => (e.id === id ? { ...e, status } : e)));
      toast.success("Status updated");
    } catch {
      toast.error("Could not update status");
    }
  };

  const formatDate = (iso) => {
    try { return new Date(iso).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F8FBFA]">
      <header className="bg-white border-b border-[#e6efee] sticky top-0 z-40">
        <div className="container-x h-16 flex items-center justify-between">
          <div className="flex items-center gap-5">
            <Logo />
            <span className="hidden sm:inline-flex pill">Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} data-testid="admin-refresh-btn" className="btn-secondary text-sm" disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button onClick={logout} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium text-[#0F2A2C] hover:bg-[#F8FBFA]">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="container-x py-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <p className="eyebrow">Admin</p>
            <h1 className="mt-2 font-serif text-3xl lg:text-4xl font-medium text-[#0F2A2C]">Submissions</h1>
            <p className="mt-1 text-[#4A5568]">All referral enquiries and contact form messages.</p>
          </div>
          <Link to="/" className="btn-ghost text-sm" data-testid="admin-back-to-site">← Back to site</Link>
        </div>

        <div className="inline-flex p-1 bg-white border border-[#e6efee] rounded-full mb-6">
          <button onClick={() => setTab("enquiries")} data-testid="admin-tab-enquiries" className={`inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold transition ${tab === "enquiries" ? "bg-[#0B6E70] text-white" : "text-[#0F2A2C]"}`}>
            <Inbox className="h-4 w-4" /> Enquiries
            <span className={`ml-1 inline-flex items-center justify-center min-w-6 h-5 px-1.5 rounded-full text-xs ${tab === "enquiries" ? "bg-white/20" : "bg-[#E6FFFA] text-[#0B6E70]"}`}>{enquiries.length}</span>
          </button>
          <button onClick={() => setTab("contacts")} data-testid="admin-tab-contacts" className={`inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold transition ${tab === "contacts" ? "bg-[#0B6E70] text-white" : "text-[#0F2A2C]"}`}>
            <MessageSquare className="h-4 w-4" /> Contacts
            <span className={`ml-1 inline-flex items-center justify-center min-w-6 h-5 px-1.5 rounded-full text-xs ${tab === "contacts" ? "bg-white/20" : "bg-[#E6FFFA] text-[#0B6E70]"}`}>{contacts.length}</span>
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-[#4A5568]">
            <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading…
          </div>
        ) : tab === "enquiries" ? (
          <div className="space-y-4" data-testid="enquiries-list">
            {enquiries.length === 0 && <EmptyState label="No enquiries yet" />}
            {enquiries.map((e) => (
              <article key={e.id} data-testid={`enquiry-${e.id}`} className="rounded-2xl bg-white border border-[#e6efee] p-6 hover:shadow-md transition">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="font-serif text-xl font-semibold text-[#0F2A2C]">{e.participant_name}</h3>
                    {e.contact_name && <p className="text-sm text-[#4A5568]">via {e.contact_name}</p>}
                  </div>
                  <select value={e.status} onChange={(ev) => updateStatus(e.id, ev.target.value)} data-testid={`status-${e.id}`} className="text-xs font-semibold rounded-full px-3 py-1.5 border border-[#d6e1e0] bg-white text-[#0B6E70]">
                    {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                  <InfoBit icon={Mail} value={e.email} />
                  <InfoBit icon={Phone} value={e.phone} />
                  <InfoBit label="NDIS" value={e.ndis_number || "—"} />
                  <InfoBit label="Plan" value={e.plan_type} />
                </div>
                {e.support_types?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {e.support_types.map((s) => <span key={s} className="text-xs px-2.5 py-1 rounded-full bg-[#E6FFFA] text-[#0B6E70] font-medium">{s}</span>)}
                  </div>
                )}
                {e.preferred_times && <p className="mt-3 text-sm text-[#4A5568]"><span className="font-semibold text-[#0F2A2C]">Preferred:</span> {e.preferred_times}</p>}
                <p className="mt-3 text-[15px] text-[#0F2A2C] whitespace-pre-wrap">{e.message}</p>
                <p className="mt-4 inline-flex items-center gap-1.5 text-xs text-[#827397]"><Calendar className="h-3.5 w-3.5" /> {formatDate(e.created_at)}</p>
              </article>
            ))}
          </div>
        ) : (
          <div className="space-y-4" data-testid="contacts-list">
            {contacts.length === 0 && <EmptyState label="No contact messages yet" />}
            {contacts.map((c) => (
              <article key={c.id} data-testid={`contact-${c.id}`} className="rounded-2xl bg-white border border-[#e6efee] p-6 hover:shadow-md transition">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <h3 className="font-serif text-xl font-semibold text-[#0F2A2C]">{c.name}</h3>
                  <p className="text-xs text-[#827397] inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> {formatDate(c.created_at)}</p>
                </div>
                <div className="mt-3 grid sm:grid-cols-3 gap-3 text-sm">
                  <InfoBit icon={Mail} value={c.email} />
                  <InfoBit icon={Phone} value={c.phone || "—"} />
                  <InfoBit label="Subject" value={c.subject || "—"} />
                </div>
                <p className="mt-3 text-[15px] text-[#0F2A2C] whitespace-pre-wrap">{c.message}</p>
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

const InfoBit = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2 min-w-0">
    {Icon && <Icon className="h-4 w-4 text-[#0B6E70] mt-0.5 flex-shrink-0" />}
    <div className="min-w-0">
      {label && <p className="text-[11px] uppercase tracking-wider text-[#827397] font-semibold">{label}</p>}
      <p className="text-[#0F2A2C] truncate">{value}</p>
    </div>
  </div>
);

const EmptyState = ({ label }) => (
  <div className="rounded-2xl bg-white border border-dashed border-[#d6e1e0] p-12 text-center text-[#4A5568]">{label}</div>
);
